type LoadImageOptions = {
  /** Extra fetch attempts after the first before falling back (default 2). */
  retries?: number;
  /** Base backoff between attempts in ms; grows linearly per attempt. */
  retryDelayMs?: number;
};

export async function loadImageFromUrl(
  url: string,
  { retries = 2, retryDelayMs = 400 }: LoadImageOptions = {}
): Promise<HTMLImageElement> {
  let lastError: unknown;

  // Retry the fetch: "Failed to fetch" is usually a transient network / CORS
  // preflight hiccup from the CDN, so a couple of quick retries recover it.
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      return await fetchImageFromUrl(url);
    } catch (error) {
      lastError = error;
      if (attempt < retries) {
        await delay(retryDelayMs * (attempt + 1));
      }
    }
  }

  // Last resort: load straight through an <img> element, which can still
  // succeed when fetch() was aborted by a transient failure. If the response
  // lacks CORS headers the canvas taints and this throws too, so we surface the
  // original fetch error instead of a corrupt preview.
  try {
    const image = await loadImageElement(url);
    return await cloneImageElement(image);
  } catch {
    throw lastError instanceof Error
      ? lastError
      : new Error("Failed to load image");
  }
}

// Fetches an image as a blob with the CORS options S3 needs. Shared with the
// card "Download Image" action so both paths get a usable blob instead of
// falling back to opening the image in a new tab.
export async function fetchImageBlob(url: string): Promise<Blob> {
  // `cache: "reload"` is critical: the card thumbnails load the same S3 image
  // via a plain <img> (no crossOrigin), so the browser caches a response with
  // no Access-Control-Allow-Origin header. A normal cors fetch then reuses that
  // poisoned cache entry and fails with "Failed to fetch". Forcing a fresh
  // request makes S3 return the CORS headers it's actually configured to send.
  const response = await fetch(url, {
    mode: "cors",
    credentials: "omit",
    cache: "reload",
  });
  if (!response.ok) {
    throw new Error(`Failed to load image: ${response.statusText}`);
  }

  return response.blob();
}

async function fetchImageFromUrl(url: string): Promise<HTMLImageElement> {
  const blob = await fetchImageBlob(url);
  const objectUrl = URL.createObjectURL(blob);

  try {
    const image = await loadImageElement(objectUrl);
    return await cloneImageElement(image);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

async function cloneImageElement(
  image: HTMLImageElement
): Promise<HTMLImageElement> {
  const width = image.naturalWidth || image.width;
  const height = image.naturalHeight || image.height;

  if (width === 0 || height === 0) {
    return image;
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d");
  if (!context) {
    return image;
  }

  context.drawImage(image, 0, 0);
  return loadImageElement(canvas.toDataURL("image/png"));
}

export function loadImageElement(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Failed to decode image"));
    image.src = src;
  });
}

export function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
        return;
      }

      try {
        const dataUrl = canvas.toDataURL("image/png");
        resolve(dataUrlToBlob(dataUrl));
      } catch (error) {
        reject(
          error instanceof Error
            ? error
            : new Error("Failed to export story image")
        );
      }
    }, "image/png");
  });
}

function dataUrlToBlob(dataUrl: string): Blob {
  const [header, base64] = dataUrl.split(",");
  const mime = header.match(/:(.*?);/)?.[1] || "image/png";
  const bytes = atob(base64);
  const buffer = new Uint8Array(bytes.length);

  for (let index = 0; index < bytes.length; index += 1) {
    buffer[index] = bytes.charCodeAt(index);
  }

  return new Blob([buffer], { type: mime });
}
